import axios from "axios";
import jwtDecoder from "../utils/jwtDecoder";
import removeClientSideStorage from "../utils/functions";
import { callExternalTrigger } from "../context/AuthContext";
import { triggerGlobalSnackbar } from "../context/GlobalSnackbarContext";
import { CHAT_BASE_URL, UPLOAD_BASE_URL } from "./networkurls";

const BaseURL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, "");


if (!BaseURL) {
  throw new Error(
    "REACT_APP_BASE_URL is not defined in your .env file. Please set it!"
  );
}


let isRedirecting = false;


const buildUrl = (url: string) => {
  if (!BaseURL) throw new Error("REACT_APP_BASE_URL not set");

  const normalizedBase = BaseURL.replace(/\/$/, "");
  const normalizedPath = url.startsWith("/") ? url : "/" + url;

  const fullUrl = normalizedBase + normalizedPath;

  const hostname = new URL(fullUrl).hostname;
  if (hostname !== new URL(BaseURL).hostname) {
    throw new Error(`🚫 Blocked unsafe request to ${fullUrl}`);
  }

  return fullUrl;
};



const handleUnauthorized = (error: any) => {
  console.error("API Error:", error);

  if (error?.response?.status === 403 || error?.message === "Session expired") {
    // Trigger session expired modal instead of immediate redirect
    callExternalTrigger();
    return true; // Return true to indicate session expired
  }
  return false; // Return false for other errors
};


const getHeaders = async (requireAuth = true, isFormData = false) => {
  const headers: Record<string, string> = {
    "Content-Type": isFormData ? "multipart/form-data" : "application/json",
  };

  if (requireAuth) {
    const jwt = await jwtDecoder();
    if (!jwt) throw new Error("Session expired");
    headers.Authorization = `Bearer ${jwt}`;
  }

  return headers;
};


const serializeError = (error: any): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e: any) => e.msg || JSON.stringify(e)).join(", ");
  if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return (
    error?.response?.data?.error ||
    error?.message ||
    "An unexpected error occurred."
  );
};

const Get = async (url: string, requireAuth = true) => {
  try {
    const headers = await getHeaders(requireAuth);
    const safeUrl = buildUrl(url);
    const response = await axios.get(safeUrl, {
      headers,
      withCredentials: true,
    });
    return response;
  } catch (error: any) {
    const isSessionExpired = handleUnauthorized(error);
    if (!isSessionExpired) {
      triggerGlobalSnackbar({ message: serializeError(error), color: "error" });
    }
    return null;
  }
};

const Post = async (
  url: string,
  payload: any,
  requireAuth = true,
  isFormData = false,
  responseType: "json" | "blob" = "json"
) => {
  try {
    const headers = await getHeaders(requireAuth, isFormData);
    const safeUrl = buildUrl(url);
    const response = await axios.post(safeUrl, payload, {
      headers,
      responseType,
      withCredentials: true,
    });
    return response;
  } catch (error: any) {
    const isSessionExpired = handleUnauthorized(error);
    if (!isSessionExpired) {
      triggerGlobalSnackbar({ message: serializeError(error), color: "error" });
    }
    return null;
  }
};

const PostWithProgress = async (
  url: string,
  payload: any,
  onUploadProgress: (progressEvent: any) => void,
  requireAuth = true,
  isFormData = false,
  responseType: "json" | "blob" = "json"
) => {
  try {
    const headers = await getHeaders(requireAuth, isFormData);
    const safeUrl = buildUrl(url);
    const response = await axios.post(safeUrl, payload, {
      headers,
      responseType,
      withCredentials: true,
      onUploadProgress,
    });
    return response;
  } catch (error: any) {
    const isSessionExpired = handleUnauthorized(error);
    if (!isSessionExpired) {
      triggerGlobalSnackbar({ message: serializeError(error), color: "error" });
    }
    return null;
  }
};

const Put = async (url: string, payload: any, requireAuth = true) => {
  try {
    const headers = await getHeaders(requireAuth);
    const safeUrl = buildUrl(url);
    const response = await axios.put(safeUrl, payload, {
      headers,
      withCredentials: true,
    });
    return { response: response.data, status: response.status };
  } catch (error: any) {
    const isSessionExpired = handleUnauthorized(error);
    if (!isSessionExpired) {
      triggerGlobalSnackbar({ message: serializeError(error), color: "error" });
    }
    return { message: serializeError(error), status: error?.response?.status || 500 };
  }
};

const Delete = async (url: string, requireAuth = true) => {
  try {
    const headers = await getHeaders(requireAuth);
    const safeUrl = buildUrl(url);
    const response = await axios.delete(safeUrl, {
      headers,
      withCredentials: true,
    });
    return response;
  } catch (error: any) {
    const isSessionExpired = handleUnauthorized(error);
    if (!isSessionExpired) {
      triggerGlobalSnackbar({ message: serializeError(error), color: "error" });
    }
    return null;
  }
};

/**
 * POST to the cb-presales-v2 chat backend (separate server from the main backend).
 * Automatically attaches the JWT from cookies as a Bearer token.
 */
const PostChat = async (path: string, payload: any, isFormData = false): Promise<any> => {
  const url = `${CHAT_BASE_URL}${path}`;

  try {
    const jwt = await jwtDecoder();
    if (!jwt) throw new Error("Session expired");

    // For FormData, leave Content-Type unset so axios/the browser can add the
    // multipart boundary itself — setting it manually here breaks upload parsing.
    const headers: Record<string, string> = { Authorization: `Bearer ${jwt}` };
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }

    const response = await axios.post(url, payload, { headers });
    return response;
  } catch (error: any) {
    const isSessionExpired = handleUnauthorized(error);
    if (!isSessionExpired) {
      triggerGlobalSnackbar({ message: serializeError(error), color: "error" });
    }
    return null;
  }
};

/**
 * GET from the cb-presales-v2 chat backend. See PostChat.
 */
const GetChat = async (path: string): Promise<any> => {
  const url = `${CHAT_BASE_URL}${path}`;

  try {
    const jwt = await jwtDecoder();
    if (!jwt) throw new Error("Session expired");

    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return response;
  } catch (error: any) {
    const isSessionExpired = handleUnauthorized(error);
    if (!isSessionExpired) {
      triggerGlobalSnackbar({ message: serializeError(error), color: "error" });
    }
    return null;
  }
};

/**
 * PUT to the cb-presales-v2 chat backend. See PostChat.
 */
const PutChat = async (path: string, payload: any): Promise<any> => {
  const url = `${CHAT_BASE_URL}${path}`;

  try {
    const jwt = await jwtDecoder();
    if (!jwt) throw new Error("Session expired");

    const response = await axios.put(url, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    });
    return response;
  } catch (error: any) {
    const isSessionExpired = handleUnauthorized(error);
    if (!isSessionExpired) {
      triggerGlobalSnackbar({ message: serializeError(error), color: "error" });
    }
    return null;
  }
};

/**
 * DELETE against the cb-presales-v2 chat backend. See PostChat.
 */
const DeleteChat = async (path: string): Promise<any> => {
  const url = `${CHAT_BASE_URL}${path}`;

  try {
    const jwt = await jwtDecoder();
    if (!jwt) throw new Error("Session expired");

    const response = await axios.delete(url, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return response;
  } catch (error: any) {
    const isSessionExpired = handleUnauthorized(error);
    if (!isSessionExpired) {
      triggerGlobalSnackbar({ message: serializeError(error), color: "error" });
    }
    return null;
  }
};

/**
 * POST to the Pre-Sales-Data-Uploader service (separate server from both the main
 * backend and the chat backend). Used for case-study/video uploads, reindexing, and
 * video-upload-status polling — endpoints that only exist on this service.
 */
const PostUpload = async (
  url: string,
  payload: any,
  requireAuth = true,
  isFormData = false
): Promise<any> => {
  const fullUrl = `${UPLOAD_BASE_URL}${url.startsWith("/") ? url : "/" + url}`;

  try {
    const headers = await getHeaders(requireAuth, isFormData);
    const response = await axios.post(fullUrl, payload, { headers });
    return response;
  } catch (error: any) {
    const isSessionExpired = handleUnauthorized(error);
    if (!isSessionExpired) {
      triggerGlobalSnackbar({ message: serializeError(error), color: "error" });
    }
    return null;
  }
};

/**
 * GET from the Pre-Sales-Data-Uploader service. See PostUpload.
 */
const GetUpload = async (url: string, requireAuth = true): Promise<any> => {
  const fullUrl = `${UPLOAD_BASE_URL}${url.startsWith("/") ? url : "/" + url}`;

  try {
    const headers = await getHeaders(requireAuth);
    const response = await axios.get(fullUrl, { headers });
    return response;
  } catch (error: any) {
    const isSessionExpired = handleUnauthorized(error);
    if (!isSessionExpired) {
      triggerGlobalSnackbar({ message: serializeError(error), color: "error" });
    }
    return null;
  }
};

export { Post, PostWithProgress, Put, Get, Delete, getHeaders, BaseURL, PostChat, GetChat, PutChat, DeleteChat, PostUpload, GetUpload };
